import { addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../firebase';

const EVENTS_COLLECTION = 'platform_events';
const ERRORS_COLLECTION = 'platform_errors';

const VIEW_THROTTLE_MS = 30_000;

/** @type {{ role: string, companyId: string | null, module: string | null, pathname: string }} */
let telemetryContext = {
  role: '',
  companyId: null,
  module: null,
  pathname: '',
};

const lastModuleView = { module: null, at: 0 };

/**
 * Update ambient context attached to every telemetry payload.
 * @param {Partial<typeof telemetryContext>} patch
 */
export function setTelemetryContext(patch) {
  telemetryContext = { ...telemetryContext, ...patch };
}

/** @returns {typeof telemetryContext} */
export function getTelemetryContext() {
  return { ...telemetryContext };
}

function basePayload() {
  const user = auth.currentUser;
  const pathname =
    telemetryContext.pathname ||
    (typeof window !== 'undefined' ? window.location.pathname : '');

  return {
    userId: user?.uid || null,
    userEmail: user?.email || '',
    role: telemetryContext.role || '',
    companyId: telemetryContext.companyId || null,
    module: telemetryContext.module || null,
    pathname,
    userAgent:
      typeof navigator !== 'undefined' ? navigator.userAgent.slice(0, 512) : '',
    createdAt: serverTimestamp(),
  };
}

function safeWrite(writeFn) {
  writeFn().catch((err) => {
    if (process.env.NODE_ENV !== 'production') {
      console.warn('[telemetry]', err);
    }
  });
}

/**
 * @param {'module_viewed' | 'permission_denied' | 'ui_error'} eventType
 * @param {Record<string, unknown>} fields
 */
export function trackEvent(eventType, fields = {}) {
  if (eventType === 'ui_error') {
    return trackUiError(fields.error instanceof Error ? fields.error : new Error(String(fields.message || 'ui_error')), {
      componentStack: typeof fields.componentStack === 'string' ? fields.componentStack : '',
      source: typeof fields.source === 'string' ? fields.source : 'telemetry',
    });
  }

  if (eventType === 'module_viewed') {
    const moduleId = fields.moduleId || fields.module;
    if (typeof moduleId === 'string') {
      trackModuleViewed(moduleId);
    }
    return;
  }

  if (eventType === 'permission_denied') {
    trackPermissionDenied({
      module: typeof fields.module === 'string' ? fields.module : undefined,
      action: typeof fields.action === 'string' ? fields.action : 'read',
      tabId: typeof fields.tabId === 'string' ? fields.tabId : undefined,
      reason: typeof fields.reason === 'string' ? fields.reason : '',
    });
  }
}

/**
 * @param {string} moduleId Dashboard tab / section id
 */
export function trackModuleViewed(moduleId) {
  if (!moduleId || !auth.currentUser) return;

  const now = Date.now();
  if (
    lastModuleView.module === moduleId &&
    now - lastModuleView.at < VIEW_THROTTLE_MS
  ) {
    return;
  }
  lastModuleView.module = moduleId;
  lastModuleView.at = now;

  setTelemetryContext({ module: moduleId });

  safeWrite(() =>
    addDoc(collection(db, EVENTS_COLLECTION), {
      ...basePayload(),
      eventType: 'module_viewed',
      moduleId,
    }),
  );
}

/**
 * @param {{ module?: string, action?: string, tabId?: string, reason?: string }} params
 */
export function trackPermissionDenied({ module, action = 'read', tabId, reason = '' }) {
  if (!auth.currentUser) return;

  safeWrite(() =>
    addDoc(collection(db, EVENTS_COLLECTION), {
      ...basePayload(),
      eventType: 'permission_denied',
      permissionModule: module || null,
      permissionAction: action,
      tabId: tabId || telemetryContext.module || null,
      reason,
    }),
  );
}

/**
 * @param {Error | unknown} error
 * @param {{ componentStack?: string, source?: string }} [meta]
 */
export function trackUiError(error, meta = {}) {
  if (!auth.currentUser) return;

  const err = error instanceof Error ? error : new Error(String(error));
  const message = err.message || 'Unknown UI error';
  const stack = err.stack || '';

  safeWrite(() =>
    addDoc(collection(db, ERRORS_COLLECTION), {
      ...basePayload(),
      eventType: 'ui_error',
      message: message.slice(0, 2000),
      stack: stack.slice(0, 8000),
      componentStack: (meta.componentStack || '').slice(0, 8000),
      source: meta.source || 'error_boundary',
    }),
  );
}
